import React from 'react';
import { Link } from 'react-router-dom';
import { PageTitle } from '../components/PageTitle';
import { ArrowLeftIcon, TrendingUpIcon, UsersIcon, LayoutIcon, DollarSignIcon, BarChartIcon } from 'lucide-react';
export function CaseStudy9() {
  return <div data-id="element-1138">
      <PageTitle title="DTC Product Selection Quiz" description="How I rebuilt a DTC guided selling tool by analyzing consumer path-to-purchase, optimizing the algorithm, and leading IT and UI teams through wireframe development to interface launch." data-id="element-1139" />
      <section className="bg-gradient-to-r from-[#0096B4] to-[#7DCCD8] text-white py-16" data-id="element-1140">
        <div className="container mx-auto px-4" data-id="element-1141">
          <Link to="/" className="inline-flex items-center text-white hover:text-[#E0F5F8] mb-6 transition-colors duration-200" data-id="element-1142">
            <ArrowLeftIcon size={16} className="mr-1" data-id="element-1143" />
            Back to Home
          </Link>
          <h1 className="text-4xl font-bold mb-4" data-id="element-1144">
            DTC Product Selection Quiz
          </h1>
          <p className="text-xl mb-6 text-white max-w-3xl" data-id="element-1145">
            How I rebuilt a DTC guided selling tool by analyzing consumer
            path-to-purchase, optimizing the algorithm, and leading IT and UI
            teams through wireframe development to interface launch.
          </p>
          <div className="flex flex-wrap gap-6 mt-8" data-id="element-1146">
            <div className="flex items-center" data-id="element-1147">
              <UsersIcon size={20} className="mr-2 text-[#E0F5F8]" data-id="element-1148" />
              <span className="text-white" data-id="element-1149">98% Completion Rate</span>
            </div>
            <div className="flex items-center" data-id="element-1150">
              <TrendingUpIcon size={20} className="mr-2 text-[#E0F5F8]" data-id="element-1151" />
              <span className="text-white" data-id="element-1152">+43% Conversion Lift</span>
            </div>
            <div className="flex items-center" data-id="element-1153">
              <DollarSignIcon size={20} className="mr-2 text-[#E0F5F8]" data-id="element-1154" />
              <span className="text-white" data-id="element-1155">+$5 Average Basket Size</span>
            </div>
          </div>
        </div>
      </section>
      <section className="py-16 bg-white" data-id="element-1156">
        <div className="container mx-auto px-4" data-id="element-1157">
          <div className="max-w-3xl mx-auto" data-id="element-1158">
            <div className="mb-12" data-id="element-1159">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1160">
                The Challenge
              </h2>
              <p className="text-gray-700 mb-4" data-id="element-1161">
                Our direct-to-consumer (DTC) platform featured a product
                selection quiz that was significantly underperforming with only
                a 25% completion rate and poor conversion metrics.
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2" data-id="element-1162">
                <li data-id="element-1163">
                  The acne category was becoming increasingly competitive with
                  new players offering strong digital experiences
                </li>
                <li data-id="element-1164">
                  We had recently launched several new products that weren't
                  included in the quiz's recommendation engine
                </li>
                <li data-id="element-1165">
                  The existing quiz's user interface was confusing and not
                  optimized for mobile devices, where most of our traffic came
                  from
                </li>
              </ul>
            </div>
            <div className="mb-12" data-id="element-1166">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1167">
                My Approach
              </h2>
              <ol className="list-decimal pl-6 space-y-6" data-id="element-1168">
                <li data-id="element-1169">
                  <div data-id="element-1170">
                    <span className="font-medium text-gray-900" data-id="element-1171">
                      Data-Driven User Journey Analysis
                    </span>
                    <ul className="list-disc pl-6 mt-2 text-gray-700 space-y-1" data-id="element-1172">
                      <li data-id="element-1173">
                        Conducted comprehensive analysis using Google Analytics
                        and Adobe Analytics to identify specific drop-off points
                      </li>
                      <li data-id="element-1174">
                        Identified correlation between certain quiz paths and
                        higher/lower conversion rates to determine the most
                        effective question sequences
                      </li>
                    </ul>
                  </div>
                </li>
                <li data-id="element-1175">
                  <div data-id="element-1176">
                    <span className="font-medium text-gray-900" data-id="element-1177">
                      R&D Partnership for Backend Optimization
                    </span>
                    <ul className="list-disc pl-6 mt-2 text-gray-700 space-y-1" data-id="element-1178">
                      <li data-id="element-1179">
                        Led the redesign of the recommendation algorithm to
                        improve accuracy and incorporate our newest products
                      </li>
                      <li data-id="element-1180">
                        Developed weighted scoring models and created
                        cross-selling logic to recommend complete regimens
                        rather than single products
                      </li>
                    </ul>
                  </div>
                </li>
                <li data-id="element-1181">
                  <div data-id="element-1182">
                    <span className="font-medium text-gray-900" data-id="element-1183">
                      Technical Team Leadership
                    </span>
                    <ul className="list-disc pl-6 mt-2 text-gray-700 space-y-1" data-id="element-1184">
                      <li data-id="element-1185">
                        Led a cross-functional team of five IT and UI engineers
                        through the complete redesign process
                      </li>
                      <li data-id="element-1186">
                        Developed detailed wireframes based on consumer testing
                        and implemented a modular architecture for easy updates
                        and A/B testing
                      </li>
                    </ul>
                  </div>
                </li>
              </ol>
            </div>
            <div className="mb-12" data-id="element-1187">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1188">
                Execution & Results
              </h2>
              <div className="bg-[#F0FAFC] p-6 rounded-lg mb-8" data-id="element-1189">
                <h3 className="text-xl font-semibold text-gray-800 mb-3" data-id="element-1190">
                  Key Improvements:
                </h3>
                <ul className="space-y-3" data-id="element-1191">
                  <li className="flex" data-id="element-1192">
                    <div className="w-2 h-2 rounded-full bg-[#0096B4] mt-2 mr-3" data-id="element-1193"></div>
                    <div data-id="element-1194">
                      <span className="font-medium text-gray-900" data-id="element-1195">
                        Streamlined Question Flow
                      </span>
                      <p className="text-gray-700" data-id="element-1196">
                        Reduced number of questions by 40% while maintaining or
                        improving recommendation accuracy
                      </p>
                    </div>
                  </li>
                  <li className="flex" data-id="element-1197">
                    <div className="w-2 h-2 rounded-full bg-[#0096B4] mt-2 mr-3" data-id="element-1198"></div>
                    <div data-id="element-1199">
                      <span className="font-medium text-gray-900" data-id="element-1200">
                        Visual Selection Interface
                      </span>
                      <p className="text-gray-700" data-id="element-1201">
                        Replaced text-heavy questions with visual selection
                        options, increasing engagement and completion on mobile
                        devices
                      </p>
                    </div>
                  </li>
                  <li className="flex" data-id="element-1202">
                    <div className="w-2 h-2 rounded-full bg-[#0096B4] mt-2 mr-3" data-id="element-1203"></div>
                    <div data-id="element-1204">
                      <span className="font-medium text-gray-900" data-id="element-1205">
                        Complete Regimen Recommendations
                      </span>
                      <p className="text-gray-700" data-id="element-1206">
                        Shifted from single product recommendations to complete
                        skincare regimens, driving higher basket value
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3" data-id="element-1207">
                Impact:
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-1208">
                <div className="bg-[#E0F5F8] p-4 rounded-lg text-center" data-id="element-1209">
                  <div className="text-3xl font-bold text-[#0096B4] mb-1" data-id="element-1210">
                    98%
                  </div>
                  <div className="text-sm text-gray-700" data-id="element-1211">Completion Rate</div>
                </div>
                <div className="bg-[#E0F5F8] p-4 rounded-lg text-center" data-id="element-1212">
                  <div className="text-3xl font-bold text-[#0096B4] mb-1" data-id="element-1213">
                    +43%
                  </div>
                  <div className="text-sm text-gray-700" data-id="element-1214">Conversion Rate</div>
                </div>
                <div className="bg-[#E0F5F8] p-4 rounded-lg text-center" data-id="element-1215">
                  <div className="text-3xl font-bold text-[#0096B4] mb-1" data-id="element-1216">
                    +$5
                  </div>
                  <div className="text-sm text-gray-700" data-id="element-1217">
                    Average Basket Increase
                  </div>
                </div>
              </div>
              <p className="text-gray-700" data-id="element-1218">
                The rebuilt quiz delivered exceptional results across all key
                metrics. Completion rate increased from 25% to 98% (+292%),
                conversion rate improved by 43%, and average basket size
                increased by over $5 due to cross-selling with full regimens.
                The quiz became a valuable source of first-party data that
                informed product development and marketing strategy, and its
                approach was leveraged to optimize quizzes across multiple
                product categories.
              </p>
            </div>
            <div data-id="element-1219">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-1220">
                Key Learnings
              </h2>
              <ol className="list-decimal pl-6 space-y-4" data-id="element-1221">
                <li data-id="element-1222">
                  <div data-id="element-1223">
                    <span className="font-medium text-gray-900" data-id="element-1224">
                      Data-driven design decisions yield measurable improvements
                    </span>
                    <p className="text-gray-700" data-id="element-1225">
                      By identifying specific drop-off points through analytics,
                      we were able to make targeted improvements that directly
                      impacted completion rates and conversion metrics.
                    </p>
                  </div>
                </li>
                <li data-id="element-1226">
                  <div data-id="element-1227">
                    <span className="font-medium text-gray-900" data-id="element-1228">
                      Visual interfaces drive higher engagement
                    </span>
                    <p className="text-gray-700" data-id="element-1229">
                      Replacing text-heavy questions with visual selection
                      options significantly increased user engagement and
                      completion rates, especially on mobile devices where most
                      users accessed the quiz.
                    </p>
                  </div>
                </li>
                <li data-id="element-1230">
                  <div data-id="element-1231">
                    <span className="font-medium text-gray-900" data-id="element-1232">
                      Regimen-based recommendations drive higher basket value
                    </span>
                    <p className="text-gray-700" data-id="element-1233">
                      By shifting from single product recommendations to
                      complete skincare regimens, we were able to significantly
                      increase average order value while providing better
                      solutions for customers.
                    </p>
                  </div>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>
      <section className="py-12 bg-[#F0FAFC]" data-id="element-1234">
        <div className="container mx-auto px-4" data-id="element-1235">
          <div className="max-w-3xl mx-auto" data-id="element-1236">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1237">
              More Case Studies
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1238">
              <Link to="/case-study-1" className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200" data-id="element-1239">
                <h3 className="text-xl font-semibold text-gray-800 mb-2" data-id="element-1240">
                  Product Turnaround Strategy
                </h3>
                <p className="text-gray-600 mb-3" data-id="element-1241">
                  Drove turnaround of $110M product platform from -12% decline
                  to +9% growth.
                </p>
                <span className="text-[#0096B4] font-medium" data-id="element-1242">
                  Read case study →
                </span>
              </Link>
              <Link to="/case-study-2" className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200" data-id="element-1243">
                <h3 className="text-xl font-semibold text-gray-800 mb-2" data-id="element-1244">
                  Go-to-Market Strategy
                </h3>
                <p className="text-gray-600 mb-3" data-id="element-1245">
                  Spearheaded new GTM strategy, driving 93% trial-to-purchase
                  conversion.
                </p>
                <span className="text-[#0096B4] font-medium" data-id="element-1246">
                  Read case study →
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>;
}